/**
 * Sorting is done by comparing the sortValue in each values metadata, treating no
 * sortValue as 0. If two values have the same sortValue, the sorting will fallback on a specified
 * field in the value (which defaults to the "name" field) and sort using it with localeCompare. So if ascending
 * is true (the default), sorting generally looks like:
 * 
 * --- all values with negative sortValue, sorted by sortValue and localeCompare ---
 * --- all values with no (or 0) sortValue, sorted by localeCompare ---
 * --- all values with positive sortValue, sorted by sortValue and localeCompare ---
 * 
 * eg: you have 2 values, { value : one, name : 'bbbb', metadata : { sortValue : 2 } } and { value : one, name : 'aaaa', metadata : { sortValue : 2 } }
 *     if ascending is true, the 2 values will be sorted as:
 *     [ { value : one, name : 'aaaa', metadata : { sortValue : 2 } }, { value : one, name : 'bbbb', metadata : { sortValue : 2 } } ]
 *     because they have the same sortValue, and localeCompare will sort 'aaaa' before 'bbbb'
 * 
 * @param {Object} sortModel the sortModel to use
 * @param {string} sortModel.field the field the sort will fallback on if sortValue isn't specified
 *                                 in a value
 * @param {string} sortModel.ascending whether or not to sort in ascending or descending order
 * @param {Array} enumValues the enum values to sort
 * @returns 
 */

export default function sortBySortValueAndStringField(sortModel, enumValues) {
  const field = sortModel?.field || 'name';
  const ascending = sortModel?.ascending == null ? true : sortModel?.ascending;
  if (!Array.isArray(enumValues)) {
    return enumValues;
  }
  return [...enumValues].sort((a, b) => {
    const aSortValue = typeof a?.metadata?.sortValue === 'number' ? a?.metadata?.sortValue : 0;
    const bSortValue = typeof b?.metadata?.sortValue === 'number' ? b?.metadata?.sortValue : 0;

    // if both have sortValue order based on sortValue
    if (typeof aSortValue === 'number' && typeof bSortValue === 'number' && aSortValue !== bSortValue) {
      // descending means higher sortValue first
      return ascending ? aSortValue - bSortValue : bSortValue - aSortValue;
    }

    // if neither have sortValue or sortValue is equal fallback on the specified field
    if (typeof a[field] !== 'string' || typeof b[field] !== 'string') {
      // eslint-disable-next-line no-console
      console.warn('EnumValueSorter is receiving a bad value for a string value to sort by.', 'Defaulting to empty string. original enumValues: ', a, b);
    }
    const aVal = typeof a[field] === 'string' ? a[field] : '';
    const bVal = typeof b[field] === 'string' ? b[field] : '';

    // descending is traditional order
    return ascending ? aVal.localeCompare(bVal) : -1 * aVal.localeCompare(bVal);
  });
}